import { useState, useCallback } from 'react';

import { DomainType, CohortCharacteristicsResultType } from '@verantos-dev/types-cohorts';

export interface ConceptGroupStats {
  id: string;
  name: string;
  count: number;
  percentage: number;
}

export interface CustomConceptSelectionState {
  isCustomMode: boolean;
  selectedConceptGroups: ConceptGroupStats[];
  availableConceptGroups: ConceptGroupStats[];
}

export interface UseCustomConceptSelectionReturn {
  // State per domain
  getSelectionState: (domain: DomainType) => CustomConceptSelectionState;

  // Actions
  enableCustomMode: (domain: DomainType) => void;
  disableCustomMode: (domain: DomainType) => void;
  setSelectedConceptGroups: (domain: DomainType, conceptGroups: ConceptGroupStats[]) => void;
  setAvailableConceptGroups: (domain: DomainType, conceptGroups: ConceptGroupStats[]) => void;

  // Computed properties
  isInCustomMode: (domain: DomainType) => boolean;
  getSelectedConceptGroupNames: (domain: DomainType) => string[];
  hasCustomSelection: (domain: DomainType) => boolean;
}

export const useCustomConceptSelection = (): UseCustomConceptSelectionReturn => {
  // State for each domain type
  const [selectionStates, setSelectionStates] = useState<
    Record<DomainType, CustomConceptSelectionState>
  >({
    [CohortCharacteristicsResultType.CC_CONDITION]: {
      isCustomMode: false,
      selectedConceptGroups: [],
      availableConceptGroups: [],
    },
    [CohortCharacteristicsResultType.CC_DRUG]: {
      isCustomMode: false,
      selectedConceptGroups: [],
      availableConceptGroups: [],
    },
    [CohortCharacteristicsResultType.CC_MEASUREMENT]: {
      isCustomMode: false,
      selectedConceptGroups: [],
      availableConceptGroups: [],
    },
    [CohortCharacteristicsResultType.CC_OBSERVATION]: {
      isCustomMode: false,
      selectedConceptGroups: [],
      availableConceptGroups: [],
    },
    [CohortCharacteristicsResultType.CC_PROCEDURE]: {
      isCustomMode: false,
      selectedConceptGroups: [],
      availableConceptGroups: [],
    },
    [CohortCharacteristicsResultType.CC_VISIT]: {
      isCustomMode: false,
      selectedConceptGroups: [],
      availableConceptGroups: [],
    },
  });

  const getSelectionState = useCallback(
    (domain: DomainType): CustomConceptSelectionState => {
      return selectionStates[domain];
    },
    [selectionStates]
  );

  const updateDomainState = useCallback(
    (domain: DomainType, updates: Partial<CustomConceptSelectionState>) => {
      setSelectionStates((prev) => ({
        ...prev,
        [domain]: {
          ...prev[domain],
          ...updates,
        },
      }));
    },
    []
  );

  const enableCustomMode = useCallback(
    (domain: DomainType) => {
      updateDomainState(domain, { isCustomMode: true });
    },
    [updateDomainState]
  );

  const disableCustomMode = useCallback(
    (domain: DomainType) => {
      updateDomainState(domain, {
        isCustomMode: false,
        selectedConceptGroups: [],
      });
    },
    [updateDomainState]
  );

  const setSelectedConceptGroups = useCallback(
    (domain: DomainType, conceptGroups: ConceptGroupStats[]) => {
      // Filter duplicates by name before setting
      const uniqueConceptGroups = conceptGroups.filter(
        (group, index, array) =>
          array.findIndex(
            (g) => g.name.toLowerCase().trim() === group.name.toLowerCase().trim()
          ) === index
      );

      updateDomainState(domain, { selectedConceptGroups: uniqueConceptGroups });
    },
    [updateDomainState]
  );

  const setAvailableConceptGroups = useCallback(
    (domain: DomainType, conceptGroups: ConceptGroupStats[]) => {
      updateDomainState(domain, { availableConceptGroups: conceptGroups });
    },
    [updateDomainState]
  );

  const isInCustomMode = useCallback(
    (domain: DomainType): boolean => {
      return selectionStates[domain].isCustomMode;
    },
    [selectionStates]
  );

  const getSelectedConceptGroupNames = useCallback(
    (domain: DomainType): string[] => {
      return selectionStates[domain].selectedConceptGroups.map((group) => group.name);
    },
    [selectionStates]
  );

  const hasCustomSelection = useCallback(
    (domain: DomainType): boolean => {
      return (
        selectionStates[domain].isCustomMode &&
        selectionStates[domain].selectedConceptGroups.length > 0
      );
    },
    [selectionStates]
  );

  return {
    getSelectionState,
    enableCustomMode,
    disableCustomMode,
    setSelectedConceptGroups,
    setAvailableConceptGroups,
    isInCustomMode,
    getSelectedConceptGroupNames,
    hasCustomSelection,
  };
};
